import { AppDispatch, AppState } from 'app/store';
import { patchAPI, postAPI, getAPI } from 'services/api/apiAsync';
import { API_ENDPOINTS } from 'utils/constants';
import { LooseObjectType } from 'utils/globalTypes';
import { removeSubmittedPiiFromMissingList } from 'utils';
import {
  setIsDbooProfileUpdated,
  setIsWatchlistProfileUpdated,
} from 'services/misc/miscSlice';
import { selectMissingPii } from 'services/session/sessionSelectors';
import { setMissingPii } from 'services/session/sessionSlice';
import {
  selectUserId,
  selectUserName,
  selectUserDob,
  selectUserLastFourSSN,
  selectUserAddress,
  selectUserPhoneNumber,
} from './userSelectors';
import { setUserSliceData } from './userSlice';

export function forgotPassword(userName: string) {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: `${API_ENDPOINTS.forgotPassword}?username=${encodeURIComponent(userName)}`,
      };
      const response = await dispatch(postAPI(config)).unwrap();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function forgotUsername(email: string) {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: `${API_ENDPOINTS.forgotUsername}?email=${encodeURIComponent(email)}`,
      };
      const response = await dispatch(postAPI(config)).unwrap();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function fetchUserDetails(currentUserId = '') {
  return async (dispatch: AppDispatch, getState: AppState) => {
    const userIdentifier = selectUserId(getState());
    const userId = currentUserId || userIdentifier;
    try {
      const config = {
        url: `${API_ENDPOINTS.userProfile}/${userId}`,
      };
      const response = await dispatch(getAPI(config)).unwrap();
      dispatch(setUserSliceData(response));
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function updateUserProfile(formData: LooseObjectType) {
  return async (dispatch: AppDispatch, getState: AppState) => {
    // capture before the pruning below mutates formData
    const submittedFields = Object.keys(formData);
    const state = getState();
    const userId = selectUserId(state);
    const userName = selectUserName(state);
    const dob = selectUserDob(state);
    const ssn = selectUserLastFourSSN(state);
    const address = selectUserAddress(state);
    const phoneNumber = selectUserPhoneNumber(state);

    if (formData.name && Object.keys(userName).length > 0) {
      delete formData.name;
    }
    if (formData.birthDate && dob) {
      delete formData.birthDate;
    }
    if (formData.ssn && ssn) {
      delete formData.ssn;
    }
    if (
      formData.address &&
      Object.values(formData.address).join('') ===
        Object.values(address).join('')
    ) {
      delete formData.address;
    }
    if (formData.phoneNumber && formData.phoneNumber === phoneNumber) {
      delete formData.phoneNumber;
    }

    try {
      if (Object.keys(formData).length === 0) {
        return Promise.resolve();
      }
      const config = {
        url: `${API_ENDPOINTS.userProfile}/${userId}`,
        data: formData,
      };
      const response = await dispatch(patchAPI(config)).unwrap();
      dispatch(setUserSliceData(response));

      // setting the flag to fetch the latest data the in dboo and watchlist pages
      dispatch(setIsDbooProfileUpdated(true));
      dispatch(setIsWatchlistProfileUpdated(true));

      // update confirmed by the API, so clear any of the submitted fields still flagged as missing
      const missingPii = selectMissingPii(getState());
      if (missingPii?.length) {
        const updatedMissingPii = removeSubmittedPiiFromMissingList(
          missingPii,
          submittedFields,
        );
        if (updatedMissingPii.length !== missingPii.length) {
          dispatch(setMissingPii(updatedMissingPii));
        }
      }
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function validateToken(token: string) {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: API_ENDPOINTS.validateToken + token,
      };
      const response = await dispatch(getAPI(config)).unwrap();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function verifyToken(token: string) {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: API_ENDPOINTS.verifyToken + token,
        data: {},
      };
      const response = await dispatch(postAPI(config)).unwrap();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function resetPassword(data: { token: string; password: string }) {
  return async (dispatch: AppDispatch) => {
    const { token, password } = data;
    const url = API_ENDPOINTS.resetPassword.replace('{token}', token);
    try {
      const config = {
        url,
        data: {
          password,
        },
      };
      const response = await dispatch(postAPI(config)).unwrap();
      return response;
    } catch (error) {
      const errMsg =
        error === 'BadRequest'
          ? 'Something went wrong. Please try again with a different password.'
          : error;
      return Promise.reject(errMsg);
    }
  };
}
